'use client';

import { useState, useEffect, lazy, Suspense } from 'react';

// Lazy load non-critical widgets
const FloatingNewsletterButton = lazy(() => import('@/components/FloatingNewsletterButton'));
const AIChatWidget = lazy(() => import('@/components/AIChatWidget'));
const ExitIntentPopup = lazy(() => import('@/components/ExitIntentPopup'));

/**
 * DeferredWidgets - Lazy loads non-critical UI widgets to improve INP
 *
 * Widgets are loaded after:
 * 1. User scrolls (indicates engagement)
 * 2. User clicks anywhere (indicates interaction)
 * 3. 3 seconds have passed (fallback)
 *
 * This keeps the main thread free during initial page load,
 * improving Interaction to Next Paint (INP) scores.
 */
export default function DeferredWidgets() {
  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => {
    // Don't load on initial render - wait for interaction or timeout
    let timeoutId: ReturnType<typeof setTimeout>;

    const triggerLoad = () => {
      setShouldLoad(true);
      cleanup();
    };

    const cleanup = () => {
      window.removeEventListener('scroll', triggerLoad);
      window.removeEventListener('click', triggerLoad);
      window.removeEventListener('touchstart', triggerLoad);
      window.removeEventListener('keydown', triggerLoad);
      clearTimeout(timeoutId);
    };

    // Use requestIdleCallback if available, otherwise setTimeout
    const scheduleLoad = () => {
      if ('requestIdleCallback' in window) {
        (window as Window & { requestIdleCallback: (cb: () => void, opts?: { timeout: number }) => number }).requestIdleCallback(
          triggerLoad,
          { timeout: 5000 }
        );
      } else {
        timeoutId = setTimeout(triggerLoad, 3000);
      }
    };

    // Listen for user interaction (passive for performance)
    window.addEventListener('scroll', triggerLoad, { once: true, passive: true });
    window.addEventListener('click', triggerLoad, { once: true });
    window.addEventListener('touchstart', triggerLoad, { once: true, passive: true });
    window.addEventListener('keydown', triggerLoad, { once: true });

    // Fallback: load after idle or 3 seconds
    scheduleLoad();

    return cleanup;
  }, []);

  if (!shouldLoad) {
    return null;
  }

  // Skip marketing widgets on native — no exit intent, no newsletter float, AI chat still available
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const cap = typeof window !== 'undefined' ? (window as any).Capacitor : undefined;
  const isNativeApp = cap?.isNativePlatform?.();

  return (
    <Suspense fallback={null}>
      {!isNativeApp && <FloatingNewsletterButton />}
      <AIChatWidget />
      {!isNativeApp && <ExitIntentPopup />}
    </Suspense>
  );
}
